// ================================================================
// ErrorBoundary — ein Fehler darf nicht die ganze App auslöschen
// ================================================================
// React hängt bei einem Fehler im Rendern den KOMPLETTEN Baum aus. Ohne
// Auffangnetz heisst das: weisse Seite. Genau das ist Mohammad passiert —
// der Assistent wechselte die Ansicht, die App verschwand, und weil die
// Assistenten-Leiste ausserhalb von React an `document.body` hängt, blieb
// ausgerechnet SIE stehen. Der Eindruck war «der Assistent spinnt», dabei
// war es ein Absturz dahinter.
//
// Zwei Aufgaben:
//   1. dem Nutzer etwas Lesbares zeigen statt Weiss — mit einem Knopf, der
//      neu lädt. Ein Absturz soll eine Unterbrechung sein, keine Sackgasse.
//   2. den Fehler SICHTBAR machen. Er landet in der Konsole UND, aufklappbar,
//      auf dem Schirm: sonst steht beim nächsten Mal wieder nur «es ging
//      kaputt» im Raum und niemand weiss, wo.
//
import { Component, type ErrorInfo, type ReactNode } from 'react';
import { t } from '../../i18n/index.js';

interface Props {
  children?: ReactNode;
  /** Woher der Absturz kam — steht in der Konsolenmeldung. */
  scope?: string;
}
interface State {
  error: Error | null;
  info: string;
  open: boolean;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null, info: '', open: false };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // eslint-disable-next-line no-console
    console.error(`[${this.props.scope || 'app'}] Absturz im Render-Baum:`, error, info.componentStack);
    this.setState({ info: info.componentStack || '' });
    // Absturz an den Server melden — auf echten Geräten sieht niemand die
    // Konsole, und «es ging kaputt» ohne Stack blieb jedes Mal unlösbar.
    // Fire-and-forget mit rohem fetch: die API-Helfer könnten selbst Teil
    // des abgestürzten Zustands sein.
    try {
      const token = localStorage.getItem('levelupwerk:token');
      if (!token) return;
      void fetch('/api/client-error', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          scope: this.props.scope || 'app',
          message: String(error?.message || error).slice(0, 500),
          componentStack: (info.componentStack || '').slice(0, 2000),
          url: window.location.href.slice(0, 300),
        }),
      }).catch(() => { /* Melden darf nie selbst stören */ });
    } catch { /* dito */ }
  }

  render() {
    const { error, info, open } = this.state;
    if (!error) return this.props.children;
    return (
      <div className="rb-crash" role="alert">
        <span className="material-symbols-rounded rb-crash-ico notranslate" aria-hidden="true">error</span>
        <h2 className="rb-crash-title">{t('uxNav.sectionError')}</h2>
        <p className="rb-crash-text">
          {t('uxNav.sectionErrorHelp')}
        </p>
        <button type="button" className="rb-crash-btn" onClick={() => this.setState({ error: null, info: '', open: false })}>
          {t('common.retry')}
        </button>
        <button
          type="button"
          className="rb-crash-more"
          onClick={() => this.setState({ open: !open })}
        >
          {t(open ? 'uxNav.hideDetails' : 'uxNav.showDetails')}
        </button>
        {open && (
          <pre className="rb-crash-detail">{String(error?.message || error)}{info ? `\n${info}` : ''}</pre>
        )}
      </div>
    );
  }
}
